/**
 * The `?imp=` hint stash of SPEC-FRONTEND-RULE-095(4) - a MODULE-SCOPE side
 * effect that runs AT IMPORT TIME.
 *
 * WHY AT IMPORT AND NOT IN AN EFFECT: "at first paint" pins no order against a
 * React effect, and AuthProvider's init effect is what fires the auth redirect.
 * That redirect's URI is built from `origin + pathname`
 * (web/src/store/AuthContext.tsx, `exchangeAuthCode` and `login`), which DROPS
 * the query string, so a failed silent SSO returns to a bare path with no
 * `?imp=`. The landing SPA's entry module (tenant and workspace `main.tsx`)
 * therefore imports this module FIRST, above every other import, and the read
 * happens before any React code exists.
 *
 * WHAT IS STORED, AND ONLY THAT: the NON-SECRET `session_ref`, in
 * `sessionStorage`, under one key, for the duration of one bootstrap. Never a
 * token, never the descriptor, never an expiry - the HLD prohibition is on the
 * JWT, and the binding reference is declared safe to show and to log. A lost
 * hint costs one redirect and never a stranded binding, because discovery is
 * the caller's own descriptor read.
 *
 * @module utils/impersonationHint
 * @see TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-003
 */

/** The one sessionStorage key this feature writes. */
export const IMPERSONATION_HINT_STORAGE_KEY = "verisa_imp_hint";

/** The query parameter the start's landing URL carries. */
export const IMPERSONATION_HINT_PARAM = "imp";

/**
 * Upper bound on a stashed reference, in characters. DERIVATION: a
 * session_ref is a UUID (36 characters); 128 leaves room for any future
 * prefixing while refusing to stash an arbitrary blob from a crafted URL. A
 * longer value is IGNORED (not truncated), because a truncated reference names
 * nothing.
 */
const MAX_HINT_LENGTH = 128;

/** Only the characters a reference can contain; anything else is not one. */
const HINT_PATTERN = /^[A-Za-z0-9_-]+$/;

/**
 * Read `?imp=` from the current URL and stash it. Exported for the tests; the
 * module runs it once on import. A URL without `?imp=` writes NOTHING, so a
 * second import on a stripped URL cannot re-stash a stale value.
 */
export function stashImpersonationHintFromUrl(): void {
  try {
    const value = new URLSearchParams(window.location.search).get(IMPERSONATION_HINT_PARAM);
    if (!value || value.length > MAX_HINT_LENGTH || !HINT_PATTERN.test(value)) return;
    window.sessionStorage.setItem(IMPERSONATION_HINT_STORAGE_KEY, value);
  } catch {
    // Storage blocked (private mode, sandboxed frame): the hint is an
    // optimisation only, so discovery proceeds without it.
  }
}

/** The stashed reference, or null. */
export function readImpersonationHint(): string | null {
  try {
    return window.sessionStorage.getItem(IMPERSONATION_HINT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Consume the hint: remove it so it is used exactly once. */
export function clearImpersonationHint(): void {
  try {
    window.sessionStorage.removeItem(IMPERSONATION_HINT_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/**
 * The current URL with `?imp=` removed, keeping every other parameter and the
 * fragment. Used by all three strippers (arrival, exit, OIDC callback).
 */
export function urlWithoutImpersonationHint(href: string = window.location.href): string {
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete(IMPERSONATION_HINT_PARAM);
    return url.pathname + url.search + url.hash;
  } catch {
    return window.location.pathname;
  }
}

// THE SIDE EFFECT. Runs once, when the entry module imports this file.
stashImpersonationHintFromUrl();
